import React, { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Truck, Package } from "lucide-react";
import { Input, TextArea, FilePicker } from "./Warehouse";

export default function Branch() {
  const [shipments, setShipments] = useState([]);
  const [received, setReceived] = useState([]);
  const [products, setProducts] = useState({});

  const load = async () => {
    const [s, r, p] = await Promise.all([
      api.get("/shipments?status=in_transit"),
      api.get("/shipments?status=received"),
      api.get("/products"),
    ]);
    setShipments(s.data);
    setReceived(r.data);
    const pm = {}; p.data.forEach(x => { pm[x.id] = x; }); setProducts(pm);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Modul Cabang Kota</p>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Terima Pengiriman</h1>
        <p className="text-sm text-slate-600 mt-1">Konfirmasi barang dengan foto bukti terima.</p>
      </div>

      <div className="space-y-3">
        {shipments.length === 0 && (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">
            <Truck size={28} className="mx-auto text-slate-400" />
            <p className="mt-2 text-sm text-slate-500">Belum ada pengiriman yang perlu diterima.</p>
          </div>
        )}
        {shipments.map((s) => (
          <ShipmentCard key={s.id} shipment={s} product={products[s.product_id]} onDone={load} />
        ))}
      </div>

      <div>
        <h2 className="text-lg font-bold text-slate-900 mb-3" style={{fontFamily:'Manrope'}}>Riwayat Penerimaan</h2>
        <div className="space-y-2">
          {received.length === 0 && <p className="text-sm text-slate-500">Belum ada penerimaan.</p>}
          {received.map((s) => (
            <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-4 flex gap-3">
              {(s.received_photo_id || s.photo_id) && <img src={fileUrl(s.received_photo_id || s.photo_id)} alt="" className="w-16 h-16 rounded-lg object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {s.selisih && s.selisih !== 0 ? (
                    <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[10px] font-bold uppercase tracking-wider">Selisih {s.selisih > 0 ? '+' : ''}{s.selisih}</span>
                  ) : (
                    <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">Cocok</span>
                  )}
                </div>
                <p className="mt-1 font-semibold text-slate-900 truncate" style={{fontFamily:'Manrope'}}>{products[s.product_id]?.name || 'Produk'}</p>
                <p className="text-sm text-slate-600">Dikirim {s.qty} · Diterima {s.received_qty ?? s.qty}</p>
                <p className="text-xs text-slate-500">{s.received_at ? new Date(s.received_at).toLocaleString('id-ID') : new Date(s.created_at).toLocaleString('id-ID')}</p>
                {s.notes && <p className="text-xs text-slate-600 mt-1">{s.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ShipmentCard({ shipment, product, onDone }) {
  const [open, setOpen] = useState(false);
  const [actualQty, setActualQty] = useState(String(shipment.qty));
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Foto bukti wajib");
    const fd = new FormData();
    fd.append("shipment_id", shipment.id);
    fd.append("actual_qty", actualQty);
    fd.append("notes", notes); fd.append("photo", file);
    const t = toast.loading("Menyimpan...");
    try {
      const { data } = await api.post("/branch/receive-shipment", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.selisih && data.selisih !== 0) {
        toast.warning(`Diterima dengan SELISIH ${data.selisih > 0 ? '+' : ''}${data.selisih} (dikirim ${data.expected_qty}, diterima ${data.qty}). Tercatat di laporan.`, { id: t });
      } else {
        toast.success("Barang diterima, jumlah cocok", { id: t });
      }
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };

  return (
    <div className="bg-white rounded-xl border-l-4 border-l-amber-500 border-y border-r border-slate-200 p-4">
      <div className="flex gap-3">
        {shipment.photo_id && <img src={fileUrl(shipment.photo_id)} alt="" className="w-16 h-16 rounded-lg object-cover" />}
        <div className="flex-1 min-w-0">
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">In Transit</span>
          <p className="mt-1 font-semibold text-slate-900 truncate" style={{fontFamily:'Manrope'}}>{product?.name || 'Produk'}</p>
          <p className="text-sm text-slate-600"><Package size={12} className="inline mr-1" />Qty: {shipment.qty}</p>
          <p className="text-xs text-slate-500">Dikirim {new Date(shipment.created_at).toLocaleString('id-ID')}</p>
        </div>
      </div>
      {!open && (
        <button data-testid={`open-receive-${shipment.id}`} onClick={() => setOpen(true)} className="mt-3 w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-lg active:scale-[0.98] transition-transform">
          Konfirmasi Terima
        </button>
      )}
      {open && (
        <form onSubmit={submit} className="mt-4 space-y-3 border-t border-slate-100 pt-4">
          <Input testid={`rc-qty-${shipment.id}`} label={`Rekap Ulang — Jumlah Diterima Aktual (gudang kirim ${shipment.qty})`} type="number" value={actualQty} onChange={setActualQty} required />
          <TextArea testid={`rc-notes-${shipment.id}`} label="Catatan" value={notes} onChange={setNotes} />
          <FilePicker testid={`rc-photo-${shipment.id}`} label="Foto Bukti Terima" file={file} setFile={setFile} inputRef={fileRef} />
          <button data-testid={`submit-receive-${shipment.id}`} type="submit" className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg">Simpan Terima</button>
        </form>
      )}
    </div>
  );
}
